import { supabase } from '../../shared/supabase';

export const STAFF_NOTIFICATION_ROLES = {
  order_received: ['captain', 'waiter', 'manager', 'owner'],
  kot_added: ['captain', 'chef', 'waiter', 'manager', 'owner'],
  order_preparing: ['chef', 'manager', 'owner'],
  order_ready: ['captain', 'waiter', 'manager', 'owner'],
  order_accepted: ['captain', 'waiter', 'manager', 'owner'],
  order_rejected: ['captain', 'waiter', 'manager', 'owner'],
  payment_success: ['cashier', 'captain', 'manager', 'owner'],
  payment_failed: ['cashier', 'manager', 'owner'],
  subscription_expired: ['manager', 'owner'],
  subscription_expiring: ['manager', 'owner'],
  subscription_renewed: ['manager', 'owner'],
  subscription_cancelled: ['manager', 'owner'],
  system: ['manager', 'owner'],
};

const DEFAULT_STAFF_NOTIFICATION_ROLES = ['captain', 'manager', 'owner'];

class NotificationService {
  constructor() {
    this.STAFF_NOTIFICATION_ROLES = STAFF_NOTIFICATION_ROLES;
  }

  /**
   * Determine targeted roles for a staff notification type.
   */
  getRolesForStaffNotification(type, overrideRoles = null) {
    if (overrideRoles?.length) return overrideRoles;
    return this.STAFF_NOTIFICATION_ROLES[type] || DEFAULT_STAFF_NOTIFICATION_ROLES;
  }

  /**
   * Fan out a restaurant notification to connected staff by role.
   */
  async createStaffNotifications(notification, restaurantId, options = {}) {
    if (!notification?.title || !notification?.message || !notification?.type) {
      throw new Error('Missing required notification fields: title, message, type');
    }
    if (!restaurantId) {
      throw new Error('restaurantId is required for staff notifications');
    }

    const roles = this.getRolesForStaffNotification(notification.type, options.roles);
    const { data: staff, error: staffError } = await supabase
      .from('restaurant_staff')
      .select('user_id, staff_role')
      .eq('restaurant_id', restaurantId)
      .eq('connected', true)
      .in('staff_role', roles);

    if (staffError) throw staffError;

    const recipients = [...new Map((staff || []).map((row) => [row.user_id, row])).values()];
    if (recipients.length === 0) {
      console.warn('[notificationService] No connected staff for notification', {
        restaurantId,
        type: notification.type,
        roles,
      });
      return [];
    }

    const rows = recipients.map((member) => ({
      title: notification.title,
      message: notification.message,
      type: notification.type,
      restaurant_id: restaurantId,
      order_id: notification.order_id || null,
      recipient_user_id: member.user_id,
      read: false,
      metadata: {
        ...(notification.metadata || {}),
        target_staff_role: member.staff_role,
      },
    }));

    const { data, error } = await supabase.from('staff_notifications').insert(rows).select();
    if (error) throw error;
    return data || [];
  }

  /**
   * Create a single notification (dynamically routes to customer_notifications or staff_notifications)
   */
  async createNotification(notification) {
    try {
      if (!notification.title || !notification.message || !notification.type) {
        throw new Error('Missing required notification fields: title, message, type');
      }

      const allowedTypes = [
        'order_received',
        'order_accepted',
        'order_preparing',
        'order_ready',
        'order_rejected',
        'kot_added',
        'payment_success',
        'payment_failed',
        'subscription_expired',
        'subscription_expiring',
        'subscription_renewed',
        'subscription_cancelled',
        'system',
      ];

      if (!allowedTypes.includes(notification.type)) {
        throw new Error(`Invalid notification type. Must be one of: ${allowedTypes.join(', ')}`);
      }

      const isStaffNotif = notification.is_admin_notification === true;
      const targetTable = isStaffNotif ? 'staff_notifications' : 'customer_notifications';

      const notificationData = {
        title: notification.title,
        message: notification.message,
        type: notification.type,
        read: notification.read || false,
        metadata: notification.metadata || {},
        order_id: notification.order_id || null,
      };

      if (isStaffNotif) {
        notificationData.restaurant_id = notification.restaurant_id;
        notificationData.recipient_user_id = notification.recipient_user_id || notification.user_id;
      } else {
        notificationData.user_id = notification.user_id;
      }

      const { data, error } = await supabase
        .from(targetTable)
        .insert([notificationData])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete a notification by ID
   */
  async deleteNotification(notificationId) {
    try {
      const { data: customerData, error: customerErr } = await supabase
        .from('customer_notifications')
        .delete()
        .eq('id', notificationId)
        .select('id');

      if (customerErr) throw customerErr;
      if (customerData && customerData.length > 0) {
        return { success: true, error: null };
      }

      const { data: staffData, error: staffErr } = await supabase
        .from('staff_notifications')
        .delete()
        .eq('id', notificationId)
        .select('id');

      if (staffErr) throw staffErr;
      return { success: (staffData?.length ?? 0) > 0, error: null };
    } catch (error) {
      return { success: false, error };
    }
  }

  /**
   * Format notification fields for presentation
   */
  formatNotificationForDisplay(notification) {
    return {
      id: notification.id,
      title: notification.title || 'Notification',
      message: notification.message || '',
      type: notification.type || 'order_received',
      read: notification.read || false,
      created_at: notification.created_at,
      updated_at: notification.updated_at,
      order_id: notification.order_id,
      user_id: notification.user_id || notification.recipient_user_id,
      metadata: notification.metadata || {},
      is_admin_notification: !!notification.recipient_user_id,
    };
  }

  /**
   * Fetch notifications targeting a customer user
   */
  async getUserNotifications(userId) {
    try {
      const { data, error } = await supabase
        .from('customer_notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fetch admin staff notifications
   */
  async getManagerNotifications(restaurantId = null, userId = null, options = {}) {
    const { adminViewAll = false } = options;

    try {
      let query = supabase
        .from('staff_notifications')
        .select('*')
        .order('created_at', { ascending: false });

      if (restaurantId) {
        query = query.eq('restaurant_id', restaurantId);
      }

      if (!adminViewAll) {
        if (!userId) return [];
        query = query.eq('recipient_user_id', userId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('[notificationService] getManagerNotifications failed:', error);
      return [];
    }
  }

  /**
   * Fetch staff notifications across an owner's portfolio (all owned restaurants).
   */
  async getOwnerPortfolioNotifications(restaurantIds, userId) {
    if (!restaurantIds?.length || !userId) return [];

    try {
      const { data, error } = await supabase
        .from('staff_notifications')
        .select('*, restaurants(name)')
        .in('restaurant_id', restaurantIds)
        .eq('recipient_user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('[notificationService] getOwnerPortfolioNotifications failed:', error);
      return [];
    }
  }

  /**
   * Fetch unread customer notifications
   */
  async getUnreadNotifications(userId) {
    try {
      const { data, error } = await supabase
        .from('customer_notifications')
        .select('*')
        .eq('user_id', userId)
        .eq('read', false)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fetch unread admin/manager notifications
   */
  async getUnreadManagerNotifications() {
    try {
      const { data, error } = await supabase
        .from('staff_notifications')
        .select('*')
        .eq('read', false)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      return [];
    }
  }

  /**
   * Mark single notification as read
   */
  async markNotificationAsRead(notificationId) {
    try {
      const { data: customerData, error: customerErr } = await supabase
        .from('customer_notifications')
        .update({ read: true, updated_at: new Date().toISOString() })
        .eq('id', notificationId)
        .select();

      if (!customerErr && customerData && customerData.length > 0) {
        return customerData[0];
      }

      const { data: staffData, error: staffErr } = await supabase
        .from('staff_notifications')
        .update({ read: true, updated_at: new Date().toISOString() })
        .eq('id', notificationId)
        .select()
        .single();

      if (staffErr) throw staffErr;
      return staffData;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Mark all customer notifications as read
   */
  async markAllNotificationsAsRead(userId, notificationIds = null) {
    try {
      let query = supabase
        .from('customer_notifications')
        .update({ read: true, updated_at: new Date().toISOString() })
        .eq('user_id', userId);

      if (notificationIds && notificationIds.length > 0) {
        query = query.in('id', notificationIds);
      } else {
        query = query.eq('read', false);
      }

      const { data, error } = await query.select();
      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Mark all manager notifications as read
   */
  async markAllManagerNotificationsAsRead(
    restaurantId = null,
    userId = null,
    options = {},
    notificationIds = null,
  ) {
    const { adminViewAll = false } = options;

    try {
      let query = supabase
        .from('staff_notifications')
        .update({ read: true, updated_at: new Date().toISOString() });

      if (notificationIds && notificationIds.length > 0) {
        query = query.in('id', notificationIds);
      } else {
        query = query.eq('read', false);
        if (restaurantId) {
          query = query.eq('restaurant_id', restaurantId);
        }
        if (!adminViewAll) {
          if (!userId)
            return { success: false, updatedCount: 0, error: new Error('userId required') };
          query = query.eq('recipient_user_id', userId);
        }
      }

      const { data, error } = await query.select('id');
      if (error) throw error;
      return { success: true, updatedCount: data?.length ?? 0, error: null };
    } catch (error) {
      console.error('[notificationService] markAllManagerNotificationsAsRead failed:', error);
      return { success: false, updatedCount: 0, error };
    }
  }

  /**
   * Mark all portfolio notifications read for an owner.
   */
  async markAllOwnerPortfolioNotificationsAsRead(restaurantIds, userId, notificationIds = null) {
    if (!restaurantIds?.length || !userId) {
      return {
        success: false,
        updatedCount: 0,
        error: new Error('restaurantIds and userId required'),
      };
    }

    try {
      let query = supabase
        .from('staff_notifications')
        .update({ read: true, updated_at: new Date().toISOString() });

      if (notificationIds?.length) {
        query = query.in('id', notificationIds);
      } else {
        query = query
          .in('restaurant_id', restaurantIds)
          .eq('recipient_user_id', userId)
          .eq('read', false);
      }

      const { data, error } = await query.select('id');
      if (error) throw error;
      return { success: true, updatedCount: data?.length ?? 0, error: null };
    } catch (error) {
      console.error(
        '[notificationService] markAllOwnerPortfolioNotificationsAsRead failed:',
        error,
      );
      return { success: false, updatedCount: 0, error };
    }
  }

  /**
   * Clear all manager notifications (delete them)
   */
  async clearAllManagerNotifications(
    restaurantId,
    userId = null,
    options = {},
    notificationIds = null,
  ) {
    const { adminViewAll = false } = options;

    if (!restaurantId && (!notificationIds || notificationIds.length === 0)) {
      return {
        success: false,
        deletedCount: 0,
        error: new Error('Restaurant ID or notification IDs is required'),
      };
    }

    try {
      let query = supabase.from('staff_notifications').delete();

      if (notificationIds && notificationIds.length > 0) {
        query = query.in('id', notificationIds);
      } else {
        query = query.eq('restaurant_id', restaurantId);
        if (!adminViewAll) {
          if (!userId) {
            return { success: false, deletedCount: 0, error: new Error('userId required') };
          }
          query = query.eq('recipient_user_id', userId);
        }
      }

      const { data, error } = await query.select('id');
      if (error) throw error;
      return { success: true, deletedCount: data?.length ?? 0, error: null };
    } catch (error) {
      console.error('[notificationService] clearAllManagerNotifications failed:', error);
      return { success: false, deletedCount: 0, error };
    }
  }

  /**
   * Delete all portfolio notifications for an owner.
   */
  async clearAllOwnerPortfolioNotifications(restaurantIds, userId, notificationIds = null) {
    if (!notificationIds?.length && (!restaurantIds?.length || !userId)) {
      return {
        success: false,
        deletedCount: 0,
        error: new Error('restaurantIds and userId required'),
      };
    }

    try {
      let query = supabase.from('staff_notifications').delete();

      if (notificationIds?.length) {
        query = query.in('id', notificationIds);
      } else {
        query = query.in('restaurant_id', restaurantIds).eq('recipient_user_id', userId);
      }

      const { data, error } = await query.select('id');
      if (error) throw error;
      return { success: true, deletedCount: data?.length ?? 0, error: null };
    } catch (error) {
      console.error('[notificationService] clearAllOwnerPortfolioNotifications failed:', error);
      return { success: false, deletedCount: 0, error };
    }
  }

  /**
   * Clear all customer notifications
   */
  async clearAllUserNotifications(userId, notificationIds = null) {
    if (!userId) {
      return { success: false, deletedCount: 0, error: new Error('User ID is required') };
    }

    try {
      let query = supabase.from('customer_notifications').delete().eq('user_id', userId);

      if (notificationIds && notificationIds.length > 0) {
        query = query.in('id', notificationIds);
      }

      const { data, error } = await query.select('id');
      if (error) throw error;
      return { success: true, deletedCount: data?.length ?? 0, error: null };
    } catch (error) {
      return { success: false, deletedCount: 0, error };
    }
  }

  /**
   * Realtime subscription for customer notifications
   */
  subscribeToUserNotifications(userId, callback) {
    return supabase
      .channel(`user-notifications-${userId}-${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'customer_notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          callback(payload.new);
        },
      )
      .subscribe();
  }

  /**
   * Realtime subscription for admin/manager notifications
   */
  subscribeToManagerNotifications(callback, restaurantId = null) {
    const channelId = `admin-notifications-${restaurantId || 'all'}-${Date.now()}`;
    const filterConfig = {
      event: '*',
      schema: 'public',
      table: 'staff_notifications',
    };
    if (restaurantId) {
      filterConfig.filter = `restaurant_id=eq.${restaurantId}`;
    }
    return supabase
      .channel(channelId)
      .on('postgres_changes', filterConfig, (payload) => {
        callback(payload);
      })
      .subscribe();
  }
}

// Instantiate the singleton
export const notificationService = new NotificationService();

// Backwards-compatible bindings
export const getRolesForStaffNotification =
  notificationService.getRolesForStaffNotification.bind(notificationService);
export const createStaffNotifications =
  notificationService.createStaffNotifications.bind(notificationService);
export const createNotification = notificationService.createNotification.bind(notificationService);
export const deleteNotification = notificationService.deleteNotification.bind(notificationService);
export const formatNotificationForDisplay =
  notificationService.formatNotificationForDisplay.bind(notificationService);
export const getUserNotifications =
  notificationService.getUserNotifications.bind(notificationService);
export const getManagerNotifications =
  notificationService.getManagerNotifications.bind(notificationService);
export const getOwnerPortfolioNotifications =
  notificationService.getOwnerPortfolioNotifications.bind(notificationService);
export const getUnreadNotifications =
  notificationService.getUnreadNotifications.bind(notificationService);
export const getUnreadManagerNotifications =
  notificationService.getUnreadManagerNotifications.bind(notificationService);
export const markNotificationAsRead =
  notificationService.markNotificationAsRead.bind(notificationService);
export const markAllNotificationsAsRead =
  notificationService.markAllNotificationsAsRead.bind(notificationService);
export const markAllManagerNotificationsAsRead =
  notificationService.markAllManagerNotificationsAsRead.bind(notificationService);
export const markAllOwnerPortfolioNotificationsAsRead =
  notificationService.markAllOwnerPortfolioNotificationsAsRead.bind(notificationService);
export const clearAllManagerNotifications =
  notificationService.clearAllManagerNotifications.bind(notificationService);
export const clearAllOwnerPortfolioNotifications =
  notificationService.clearAllOwnerPortfolioNotifications.bind(notificationService);
export const clearAllUserNotifications =
  notificationService.clearAllUserNotifications.bind(notificationService);
export const subscribeToUserNotifications =
  notificationService.subscribeToUserNotifications.bind(notificationService);
export const subscribeToManagerNotifications =
  notificationService.subscribeToManagerNotifications.bind(notificationService);

export default notificationService;
